import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { usePrimeVue } from 'primevue/config'
import { i18n, SupportedLocales, DefaultLocale, type SupportedLocale } from '@/i18n'
import { trPrimeVue } from '@/i18n/primevue/tr'
import { enPrimeVue } from '@/i18n/primevue/en'

const LOCALE_KEY = 'locale'

const primeVueLocales: Record<SupportedLocale, typeof trPrimeVue> = {
  tr: trPrimeVue,
  en: enPrimeVue,
}

export const useLocaleStore = defineStore('locale', () => {
  // State
  const locale = ref<SupportedLocale>(
    (localStorage.getItem(LOCALE_KEY) as SupportedLocale) || DefaultLocale
  )

  /**
   * Set locale and persist to localStorage
   */
  function setLocale(newLocale: SupportedLocale) {
    if (!SupportedLocales.includes(newLocale)) {
      // Silently ignore unsupported locale — the caller path is type-narrowed
      // and reaching here means a user-supplied or persisted value is stale.
      // No-op preserves the previously-resolved locale; surfacing via Sentry
      // breadcrumb would be appropriate if observability is desired here.
      return
    }

    locale.value = newLocale
    ;(i18n.global.locale as unknown as Ref<SupportedLocale>).value = newLocale
    localStorage.setItem(LOCALE_KEY, newLocale)

    // Update PrimeVue locale (accessed lazily to avoid calling usePrimeVue at store setup time)
    const primevue = usePrimeVue()
    if (primevue.config.locale) {
      Object.assign(primevue.config.locale, primeVueLocales[newLocale])
    }

    // Update HTML lang attribute
    document.documentElement.lang = newLocale
  }

  /**
   * Toggle between supported locales
   */
  function toggleLocale() {
    const currentIndex = SupportedLocales.indexOf(locale.value)
    const nextIndex = (currentIndex + 1) % SupportedLocales.length
    const nextLocale = SupportedLocales[nextIndex]
    if (nextLocale) {
      setLocale(nextLocale)
    }
  }

  /**
   * Initialize locale on app startup
   */
  function initialize() {
    // PrimeVue locale must be set INSIDE a component setup() (App.vue), not
    // from top-level main.ts — usePrimeVue() uses inject() which has no
    // context outside a component instance. Try-catch lets main.ts call this
    // for the document.documentElement.lang part; App.vue calls it again
    // (idempotent) inside its setup for the PrimeVue locale binding.
    // The vue-i18n instance resolves its own initial locale (localStorage →
    // browser language → default), which can diverge from this store's
    // resolved locale (localStorage → default, no browser guess). Force the
    // i18n locale to match the store so the visible UI language always agrees
    // with the switcher on first paint — otherwise the switcher shows TR while
    // content renders in the browser language (EN) until the user re-selects.
    ;(i18n.global.locale as unknown as Ref<SupportedLocale>).value = locale.value

    try {
      const primevue = usePrimeVue()
      if (primevue?.config?.locale) {
        Object.assign(primevue.config.locale, primeVueLocales[locale.value])
      }
    } catch {
      // outside a component context — App.vue will retry
    }
    document.documentElement.lang = locale.value
  }

  return {
    // State
    locale,
    supportedLocales: SupportedLocales,

    // Actions
    setLocale,
    toggleLocale,
    initialize,
  }
})
